import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

export interface IDebugRequestLog {
  id: number;
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody?: any;
  status?: number;
  statusText?: string;
  responseBody?: any;
  error?: any;
  startedAt: string;
  finishedAt?: string;
  durationMs?: number;
  pending: boolean;
}

// Общий журнал HTTP-запросов и снапшот авторизационного состояния — пишется
// из HttpConfigInterceptor (все реквесты идут через него) и читается
// DebugPanelComponent (плавающая кнопка-дебагер, см. app.component.html).
// Нужен, чтобы на реальном мобильном устройстве (где нет доступа к devtools)
// можно было увидеть, что реально пришло в куках/сторадже и что вернул бэк —
// баг "как будто не авторизован" воспроизводится только там.
@Injectable({providedIn: 'root'})
export class DebugLogService {
  private readonly MAX_ENTRIES = 300;
  private nextId = 1;
  private logsSubject = new BehaviorSubject<IDebugRequestLog[]>([]);
  logs$ = this.logsSubject.asObservable();

  logRequest(method: string, url: string, requestHeaders: Record<string, string>, requestBody: any): number {
    const id = this.nextId++;
    const entry: IDebugRequestLog = {
      id,
      method,
      url,
      requestHeaders,
      requestBody,
      startedAt: new Date().toISOString(),
      pending: true
    };
    this.logsSubject.next([entry, ...this.logsSubject.value].slice(0, this.MAX_ENTRIES));
    return id;
  }

  completeRequest(id: number, status: number, statusText: string, responseBody: any): void {
    this.updateEntry(id, e => ({
      ...e,
      status,
      statusText,
      responseBody,
      pending: false,
      finishedAt: new Date().toISOString(),
      durationMs: Date.now() - new Date(e.startedAt).getTime()
    }));
  }

  failRequest(id: number, err: any): void {
    this.updateEntry(id, e => ({
      ...e,
      status: err?.status,
      statusText: err?.statusText,
      error: this.serializeError(err),
      pending: false,
      finishedAt: new Date().toISOString(),
      durationMs: Date.now() - new Date(e.startedAt).getTime()
    }));
  }

  clear(): void {
    this.logsSubject.next([]);
  }

  get snapshot(): IDebugRequestLog[] {
    return this.logsSubject.value;
  }

  private updateEntry(id: number, updater: (e: IDebugRequestLog) => IDebugRequestLog): void {
    this.logsSubject.next(this.logsSubject.value.map(e => e.id === id ? updater(e) : e));
  }

  private serializeError(err: any): any {
    try {
      return {message: err?.message, error: err?.error, status: err?.status, statusText: err?.statusText, url: err?.url};
    } catch {
      return String(err);
    }
  }
}
